import { useState, useMemo } from "react";
import { X, PieChart } from "lucide-react";
import type { Language } from "../data";
import type { VitKey } from "../types";
import { fridgeIngredients } from "../data";
import { vitaminsMeta, vitaminDeficiency, vitaminsPer100g, caloriesPer100g } from "../data/vitamins";
import { getIngredientName } from "../utils/recipe";
import { emojiUrl, fluentEmojiMap } from "../utils/foodImages";

interface NutritionComparePanelProps {
  lang: Language;
  dir: "rtl" | "ltr";
  imageOverrides?: Record<string, string>;
  picks: string[];
  onClose: () => void;
}

const ingredientImg = (id: string, icon: string, overrides?: Record<string, string>) =>
  overrides?.[id] ?? fluentEmojiMap[id] ?? emojiUrl(icon);

export function NutritionComparePanel({ lang, dir, imageOverrides, picks, onClose }: NutritionComparePanelProps) {
  const [mode, setMode] = useState<"cal" | "vit">("cal");
  const [activeVit, setActiveVit] = useState<VitKey>("c");

  const label = (ar: string, en: string) => lang === "ar" ? ar : en;

  const calComparison = useMemo(() =>
    picks.map(id => {
      const ing = fridgeIngredients.find(i => i.id === id)!;
      return { id, name: getIngredientName(ing, lang), icon: ing.icon, img: ingredientImg(id, ing.icon, imageOverrides), val: caloriesPer100g[id] ?? 0 };
    }).sort((a, b) => a.val - b.val),
  [picks, lang]);

  const vitComparison = useMemo(() =>
    picks.map(id => {
      const ing = fridgeIngredients.find(i => i.id === id)!;
      return { id, name: getIngredientName(ing, lang), icon: ing.icon, img: ingredientImg(id, ing.icon, imageOverrides), val: vitaminsPer100g[id]?.[activeVit] ?? 0 };
    }).sort((a, b) => b.val - a.val),
  [picks, lang, activeVit]);

  const comparison = mode === "cal" ? calComparison : vitComparison;
  const maxVal = comparison.length ? Math.max(...comparison.map(c => c.val), 1) : 1;
  const vitMeta = vitaminsMeta.find(v => v.key === activeVit)!;

  if (picks.length === 0) return null;

  return (
    <div className="rounded-2xl border-2 border-amber-300 dark:border-amber-500/40 bg-white dark:bg-slate-900 shadow-lg overflow-hidden" dir={dir}>
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-amber-100 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
            <PieChart className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-bold text-stone-800 dark:text-slate-100 text-sm leading-tight">
              {label("مقارنة القيم الغذائية", "Nutrition Compare")}
            </h3>
            <p className="text-[11px] text-stone-400 dark:text-slate-500">
              {label("لكل 100 غرام", "Per 100g")}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-full bg-stone-100 dark:bg-slate-700 flex items-center justify-center hover:bg-stone-200 dark:hover:bg-slate-600 transition-colors">
          <X className="w-4 h-4 text-stone-500 dark:text-slate-300" />
        </button>
      </div>

      <div className="px-4 pt-2.5 pb-1">
        <div className="flex gap-2 bg-stone-100 dark:bg-slate-800 rounded-xl p-1">
          <button onClick={() => setMode("cal")} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === "cal" ? "bg-amber-500 text-white" : "text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200"}`}>
            {label("السعرات الحرارية", "Calories")}
          </button>
          <button onClick={() => setMode("vit")} className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === "vit" ? "bg-emerald-500 text-white" : "text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200"}`}>
            {label("الفيتامينات والمعادن", "Vitamins & Minerals")}
          </button>
        </div>
      </div>

      {mode === "vit" && (
        <div className="px-4 pt-2 pb-1">
          <p className="text-[10px] text-stone-400 dark:text-slate-500 mb-1.5">{label("اختر فيتامين لعرض أعراض نقصه", "Select a vitamin to see deficiency info")}</p>
          <div className="flex flex-wrap gap-1.5">
            {vitaminsMeta.map(v => (
              <button
                key={v.key}
                onClick={() => setActiveVit(v.key)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${activeVit === v.key ? `${v.color} text-white border-transparent shadow` : "border-stone-200 dark:border-slate-600 text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200 hover:border-stone-300 dark:hover:border-slate-500"}`}
              >
                {lang === "ar" ? v.ar : v.en}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === "vit" && (
        <div className="px-4 pt-2.5 pb-2">
          <div className="rounded-2xl bg-emerald-50 dark:bg-slate-800 border border-emerald-100 dark:border-slate-700 p-3 space-y-2">
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full text-white ${vitMeta.color}`}>
                {lang === "ar" ? vitMeta.ar : vitMeta.en}
              </span>
              <p className="text-xs text-stone-700 dark:text-slate-300 leading-relaxed flex-1">
                {label(vitaminDeficiency[activeVit].role.ar, vitaminDeficiency[activeVit].role.en)}
              </p>
            </div>
            <p className="text-[10px] font-bold text-stone-400 dark:text-slate-500 uppercase tracking-widest">
              {label("أعراض النقص", "Deficiency Symptoms")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(lang === "ar" ? vitaminDeficiency[activeVit].ar : vitaminDeficiency[activeVit].en).map((s, i) => (
                <span
                  key={i}
                  className="symptom-glow text-[11px] px-2.5 py-1 rounded-full bg-rose-600 text-white border border-rose-700 leading-tight font-semibold"
                  style={{ animationDelay: `${i * 0.35}s` }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-2 space-y-3">
        {mode === "vit" && vitComparison.length >= 1 && (
          <div className="space-y-2">
            <p className="text-[11px] font-bold text-stone-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              {label(`مقارنة ${vitMeta.ar} لكل 100غ`, `${vitMeta.en} per 100g`)}
            </p>
            {vitComparison.map((item, idx) => {
              const maxV = vitComparison.length ? Math.max(...vitComparison.map(c => c.val), 1) : 1;
              const pct = maxV > 0 ? (item.val / maxV) * 100 : 0;
              return (
                <div key={item.id} className="flex items-center gap-2.5">
                  <img src={item.img} alt={item.name} className="w-10 h-10 object-contain shrink-0 rounded-lg bg-stone-100 dark:bg-slate-700/40 p-0.5" onError={e => { (e.target as HTMLImageElement).src = emojiUrl(item.icon); }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-bold text-stone-700 dark:text-slate-200 truncate">{item.name}</span>
                        {idx === 0 && item.val > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 shrink-0">
                            {label("الأعلى", "Highest")}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-black text-stone-800 dark:text-white shrink-0 ms-2 tabular-nums">
                        {item.val > 0 ? <>{item.val}<span className="text-xs font-normal text-stone-400 dark:text-slate-400"> {vitMeta.unit}</span></> : <span className="text-xs text-stone-400 dark:text-slate-500">{label("لا بيانات", "N/A")}</span>}
                      </span>
                    </div>
                    <div className="h-2 bg-stone-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${item.val > 0 ? vitMeta.color : "bg-stone-300 dark:bg-slate-600"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mode === "vit" && picks.length >= 1 && (
          <div className="space-y-2">
            <p className="text-[11px] font-bold text-stone-400 dark:text-slate-500 uppercase tracking-widest">
              {label("محتوى الأصناف المختارة (لكل 100غ)", "Nutrients per 100g")}
            </p>
            {picks.map(id => {
              const ing = fridgeIngredients.find(i => i.id === id)!;
              const vitData = vitaminsPer100g[id] || {};
              const presentVits = vitaminsMeta.filter(v => (vitData[v.key] ?? 0) > 0);
              return (
                <div key={id} className="rounded-xl bg-stone-50 dark:bg-slate-800 border border-stone-100 dark:border-slate-700 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <img src={ingredientImg(ing.id, ing.icon, imageOverrides)} alt={ing.nameAr} className="w-10 h-10 object-contain rounded-lg bg-white dark:bg-slate-700/40 p-0.5" onError={e => { (e.target as HTMLImageElement).src = emojiUrl(ing.icon); }} />
                    <span className="text-sm font-bold text-stone-800 dark:text-white">{getIngredientName(ing, lang)}</span>
                  </div>
                  {presentVits.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {presentVits.map(v => (
                        <span key={v.key} className={`text-[11px] px-2 py-1 rounded-lg font-bold text-white ${v.color} flex items-center gap-1`}>
                          <span>{lang === "ar" ? v.ar : v.en}</span>
                          <span className="opacity-80">{vitData[v.key]}{v.unit}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-400 dark:text-slate-500">{label("لا تتوفر بيانات غذائية", "No nutrient data available")}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {mode === "cal" && calComparison.length >= 1 && (
          <div className="space-y-2">
            <p className="text-[11px] font-bold text-stone-400 dark:text-slate-500 uppercase tracking-widest mb-2">
              {label("السعرات الحرارية لكل 100غ", "Calories per 100g")}
            </p>
            {calComparison.map(item => {
              const pct = maxVal > 0 ? (item.val / maxVal) * 100 : 0;
              const barColor = item.val <= 50 ? "bg-emerald-500" : item.val <= 150 ? "bg-amber-500" : "bg-red-500";
              const calLabel = item.val <= 50 ? label("منخفض", "Low") : item.val <= 150 ? label("معتدل", "Moderate") : label("مرتفع", "High");
              const labelColor = item.val <= 50 ? "text-emerald-600 dark:text-emerald-400" : item.val <= 150 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
              return (
                <div key={item.id} className="flex items-center gap-2.5">
                  <img src={item.img} alt={item.name} className="w-10 h-10 object-contain shrink-0 rounded-lg bg-stone-100 dark:bg-slate-700/40 p-0.5" onError={e => { (e.target as HTMLImageElement).src = emojiUrl(item.icon); }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs font-bold text-stone-700 dark:text-slate-200 truncate">{item.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-slate-700 shrink-0 ${labelColor}`}>{calLabel}</span>
                      </div>
                      <span className="text-sm font-black text-stone-800 dark:text-white shrink-0 ms-2 tabular-nums">
                        {item.val}<span className="text-xs font-normal text-stone-400 dark:text-slate-400"> kcal</span>
                      </span>
                    </div>
                    <div className="h-2 bg-stone-200 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
